/**
 * A tab container component.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam Amplify 2025
 */
import { logger } from '@/config/logger'
import { ReactNode, useEffect, useState } from 'react'
import './TabContainer.css'

interface TabProps {
  title: string
  className: string
  isSelected: boolean
  id?: string
  onClick: () => void
}

const Tab = (props: TabProps) => {
  return (
    <div
      id={props.id}
      className={`
        ${props.className}
        ${props.isSelected ? 'selected' : 'unselected'}`}
      onClick={props.onClick} // Handle click
    >
      {props.title}
    </div>
  )
}

interface TabContainerProps {
  tabList: string[]
  children: ReactNode[] // Accepts multiple children, one for each tab
  customComp?: ReactNode
  selectTabIndex?: number
}

const TabContainer = (props: TabContainerProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(
    props.selectTabIndex ?? 0
  )

  useEffect(() => {
    logger.debug('selectedIndex:', selectedIndex)
    logger.debug('props.selectTabIndex:', props.selectTabIndex)
  }, [selectedIndex, props.selectTabIndex])

  return (
    <div className="tab-container">
      <div className="tab-bar">
        {props.tabList.map((tabTitle, index) => (
          <Tab
            className="tab"
            id={`tab-${tabTitle.replaceAll(' ', '-').toLowerCase()}`}
            key={index}
            title={tabTitle}
            isSelected={selectedIndex === index}
            onClick={() => setSelectedIndex(index)} // Update selected tab
          />
        ))}
        {props.customComp && <>{props.customComp}</>}
      </div>

      {/* Display the corresponding content */}
      {props.children.length > selectedIndex && (
        <div className="tab-content">{props.children[selectedIndex]}</div>
      )}
    </div>
  )
}

export default TabContainer
